import React, { useMemo } from "react";
import Link from "next/link";
import { useOptimizedAssetUrl } from "domains/file-manager/hooks/useOptimizedAssetUrl";
import { mapAssetsToImagesFiles } from "domains/file-manager/interfaces";
import useRouter from "domains/navigation/hooks/useRouter";
import { extraTheme } from "domains/theme";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import {
  GetModelsByModelIdApiResponse,
  GetModelsExamplesByModelIdApiResponse,
} from "infra/api/generated/api";

import {
  Box,
  Flex,
  HStack,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuList,
  Skeleton,
  Text,
} from "@chakra-ui/react";

interface ModelImageExampleProps {
  model: GetModelsByModelIdApiResponse["model"];
  assetExample: GetModelsExamplesByModelIdApiResponse["examples"][0];
  imageIndex: number;
  isModelOwned: boolean;
  isOnLoraCatalog?: boolean;
  onOpenAssetSelectorAndSetIndex: (index: number) => void;
  handleOnRemoveExample: (indexImage: number) => void;
  handleOnSeeMore: () => void;
}

const ModelImageExample: React.FC<ModelImageExampleProps> = ({
  model,
  assetExample,
  imageIndex,
  isModelOwned,
  isOnLoraCatalog,
  onOpenAssetSelectorAndSetIndex,
  handleOnRemoveExample,
  handleOnSeeMore,
}) => {
  const router = useRouter();
  const assetFile = useMemo(
    () => mapAssetsToImagesFiles([assetExample.asset])[0],
    [assetExample]
  );
  const { url: optimizedUrl } = useOptimizedAssetUrl({
    file: assetFile,
    cardWidth: 512,
  });

  const showViewMore = imageIndex === 3 && assetExample?.asset.url;

  return (
    <Flex flex={1}>
      <Box
        key={assetExample?.asset.id}
        pos="relative"
        w="100%"
        h="100%"
        borderWidth={1}
        rounded="lg"
      >
        <Link
          href={{
            pathname: router.pathname,
            query: {
              ...router.query,
              openAssetId: assetFile.id,
            },
          }}
        >
          <Image
            w="100%"
            h="100%"
            objectFit="cover"
            alt="image model example"
            aspectRatio="1"
            fallback={<Skeleton w="100%" h="100%" />}
            rounded="lg"
            src={optimizedUrl}
          />
        </Link>
        {isModelOwned && assetExample?.asset.url && (
          <Box
            pos="absolute"
            top={1}
            right={1}
            borderRadius="md"
            bgColor="background"
          >
            <Menu>
              <MenuButton
                as={IconButton}
                zIndex={2}
                size={"xs"}
                variant="secondary"
              >
                <Icon id="Ui/Dots" />
              </MenuButton>
              <MenuList>
                <MenuItem
                  onClick={() => onOpenAssetSelectorAndSetIndex(imageIndex)}
                  text="Update"
                />
                <MenuItem
                  onClick={() => handleOnRemoveExample(imageIndex)}
                  text="Remove"
                />
                {!isOnLoraCatalog &&
                  assetExample.asset.metadata.inferenceId && (
                    <MenuItem
                      text="Reload Parameters"
                      internalLink={
                        assetExample.asset.metadata.inferenceId
                          ? {
                              pathname: "/images/new",
                              query: {
                                importAssetId: `${model.id}|${assetExample.asset.metadata.inferenceId}`,
                                modelId: model.id,
                              },
                            }
                          : {}
                      }
                    />
                  )}
              </MenuList>
            </Menu>
          </Box>
        )}
        {showViewMore && (
          <HStack
            pos="absolute"
            top={0}
            align="center"
            justify="center"
            w="full"
            h="full"
            bg="blackAlpha.800"
            _hover={{ bg: "blackAlpha.700" }}
            cursor="pointer"
            transition={extraTheme.transitions.fast}
            onClick={handleOnSeeMore}
            rounded="lg"
            spacing={2}
          >
            {/**
              * This is used in the model view, but now our models can generate much more than just images
              * We have videos, 3d, etc.
              */}
            <Text size="body.md">All</Text>
            <Icon id="Ui/ChevronRight" />
          </HStack>
        )}
      </Box>
    </Flex>
  );
};

export default ModelImageExample;
